// ============================================================================
// GOOGLE SHEETS SERVICE
// Fetches historical price data from Google Sheets
// ============================================================================

import { DATA_CONFIG } from '../config/dataConfig';

// Cache for Google Sheets data
let sheetsCache = {};
let cacheTimestamp = {};

/**
 * Fetch data from Google Sheets for a specific asset
 * @param {string} assetId - Asset identifier (gold, bitcoin, silver)
 * @returns {Promise<Array>} Array of {date, price} objects
 */
export const fetchGoogleSheetsData = async (assetId) => {
    try {
        // Check cache first
        if (DATA_CONFIG.cache.enabled && isCacheValid(assetId)) {
            console.log(`[GoogleSheets] Using cached data for ${assetId}`);
            return sheetsCache[assetId];
        }

        const { spreadsheetId, apiKey, sheetNames } = DATA_CONFIG.googleSheets;
        const sheetName = sheetNames[assetId];

        if (!sheetName) {
            throw new Error(`No sheet name configured for asset: ${assetId}`);
        }

        if (!apiKey) {
            console.warn('[GoogleSheets] API key not configured. Please set REACT_APP_GOOGLE_SHEETS_API_KEY environment variable.');
            return [];
        }

        // Google Sheets API v4 endpoint
        const range = `${sheetName}!A:B`; // Assuming columns A (Date) and B (Price)
        const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}?key=${apiKey}`;

        console.log(`[GoogleSheets] Fetching data for ${assetId} from sheet: ${sheetName}`);

        const response = await fetch(url);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Google Sheets API error: ${errorData.error?.message || response.statusText}`);
        }

        const data = await response.json();
        const rows = data.values || [];

        if (rows.length === 0) {
            console.warn(`[GoogleSheets] No data found in sheet: ${sheetName}`);
            return [];
        }

        // Parse the data (skip header row)
        const parsedData = [];
        for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (row.length >= 2 && row[0] && row[1]) {
                const dateStr = row[0].trim();
                const priceStr = row[1].trim();

                // Validate and parse
                const price = parseFloat(priceStr);
                if (!isNaN(price) && isValidDate(dateStr)) {
                    parsedData.push({
                        date: normalizeDate(dateStr),
                        price: price
                    });
                }
            }
        }

        // Sort by date (oldest first)
        parsedData.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Cache the data
        if (DATA_CONFIG.cache.enabled) {
            sheetsCache[assetId] = parsedData;
            cacheTimestamp[assetId] = Date.now();
        }

        console.log(`[GoogleSheets] Successfully fetched ${parsedData.length} records for ${assetId}`);
        return parsedData;

    } catch (error) {
        console.error(`[GoogleSheets] Error fetching data for ${assetId}:`, error);

        // Return cached data if available, even if expired
        if (sheetsCache[assetId]) {
            console.warn(`[GoogleSheets] Returning stale cached data for ${assetId}`);
            return sheetsCache[assetId];
        }

        throw error;
    }
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (assetId) => {
    if (!sheetsCache[assetId] || !cacheTimestamp[assetId]) {
        return false;
    }

    const age = Date.now() - cacheTimestamp[assetId];
    return age < DATA_CONFIG.cache.ttl;
};

/**
 * Validate date string
 */
const isValidDate = (dateStr) => {
    const date = new Date(dateStr);
    return date instanceof Date && !isNaN(date);
};

/**
 * Normalize date to YYYY-MM-DD format
 */
const normalizeDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
};

/**
 * Clear cache for specific asset or all assets
 */
export const clearCache = (assetId = null) => {
    if (assetId) {
        delete sheetsCache[assetId];
        delete cacheTimestamp[assetId];
        console.log(`[GoogleSheets] Cache cleared for ${assetId}`);
    } else {
        sheetsCache = {};
        cacheTimestamp = {};
        console.log('[GoogleSheets] All cache cleared');
    }
};
